/**
 * client/input/rumble.ts — rumble mixer (pure, DOM-free, unit-tested).
 *
 * OWNER: INPUT agent.
 * Data: research/data/controller.json rumble/* defs + *.rmb graphs (HIGH) [CTL §7].
 * Playback (ESTIMATE, IW convention [CTL §7]):
 *  - lowRumbleFile -> large low-frequency motor = DualSense LEFT / "strong";
 *    highRumbleFile -> small motor = RIGHT / "weak";
 *  - graphs are (t 0..1 over `duration`, intensity);
 *  - range 1.0 = on self; broadcast rumbles with a range play when within range, scaled by
 *    1 - dist/range when fadeWithDistance is set;
 *  - overlapping rumbles: per-motor max.
 */
import { RUMBLE_GRAPHS, RUMBLES, type RumbleDef } from './controller-data.generated';
import { graphValue } from './stick';

interface ActiveRumble { def: RumbleDef; start: number; scale: number }

export interface MotorLevels { strong: number; weak: number }

export class RumbleMixer {
  private readonly active: ActiveRumble[] = [];

  /** Start a rumble by name. `scale` multiplies both motors (distance fade is applied by playAt). */
  play(name: string, now: number, scale = 1): boolean {
    const def = RUMBLES[name];
    if (!def || scale <= 0) return false;
    this.active.push({ def, start: now, scale: Math.min(1, scale) });
    if (this.active.length > 16) this.active.shift();
    return true;
  }

  /** Rumble at a world position (playrumbleonposition): range test + optional fade. */
  playAt(name: string, now: number, dist: number): boolean {
    const def = RUMBLES[name];
    if (!def) return false;
    if (def.range > 1) {
      if (dist > def.range) return false;
      const s = def.fadeWithDistance ? 1 - dist / def.range : 1;
      return this.play(name, now, s);
    }
    return this.play(name, now, 1);
  }

  stopAll(): void { this.active.length = 0; }

  get playing(): boolean { return this.active.length > 0; }

  /** Current per-motor level (0..1), dropping finished rumbles. */
  sample(now: number, out: MotorLevels): MotorLevels {
    let strong = 0, weak = 0;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const r = this.active[i];
      const t = (now - r.start) / (r.def.duration * 1000);
      if (t >= 1 || t < 0) { if (t >= 1) this.active.splice(i, 1); continue; }
      const lo = RUMBLE_GRAPHS[r.def.low], hi = RUMBLE_GRAPHS[r.def.high];
      if (lo) strong = Math.max(strong, graphValue(lo, t) * r.scale);
      if (hi) weak = Math.max(weak, graphValue(hi, t) * r.scale);
    }
    out.strong = Math.min(1, Math.max(0, strong));
    out.weak = Math.min(1, Math.max(0, weak));
    return out;
  }
}
